import { useMemo, useState, type ReactNode } from 'react';
import { css, type SerializedStyles } from '@emotion/react';
import type { Artifact, Train } from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import dayjs from 'dayjs';
import { Button, Pagination, SearchField, Text, useControlledField } from '@frontend/design-system';
import { formatDate, getDisplayValue } from '../shared/helpers';

const ARTIFACTS_PER_PAGE = 12;

interface TrainDetailViewProps {
  train: Train;
  onBack: () => void;
}

interface MetaItemProps {
  label: string;
  value?: string | null;
}

export const TrainDetailView = ({ train, onBack }: TrainDetailViewProps) => {
  const artifacts = useMemo(() => train.artifacts ?? [], [train.artifacts]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const searchFieldProps = useControlledField({
    type: 'text',
    value: searchQuery,
    onChange: (value) => {
      setSearchQuery(value);
      setPage(1);
    },
  });
  const filteredArtifacts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase();

    if (!normalizedQuery) return artifacts;

    return artifacts.filter(
      ({ packageName, artifactId, versionTag }) =>
        packageName?.toLocaleLowerCase().includes(normalizedQuery) ||
        artifactId?.toLocaleLowerCase().includes(normalizedQuery) ||
        versionTag?.toLocaleLowerCase().includes(normalizedQuery)
    );
  }, [artifacts, searchQuery]);
  const totalPages = Math.ceil(filteredArtifacts.length / ARTIFACTS_PER_PAGE);
  const visibleArtifacts = filteredArtifacts.slice((page - 1) * ARTIFACTS_PER_PAGE, page * ARTIFACTS_PER_PAGE);

  return (
    <div css={wrapperStyle}>
      <div css={headerRowStyle}>
        <Button iconName='arrow-left' hoverLabel='Back to trains' onClick={onBack} variant='tertiary' />
        <div css={titleBlockStyle}>
          <div css={titleRowStyle}>
            <Text weight='bold' size='large'>
              {train.trainName}
            </Text>
            {train.promotedChannel?.trim() ? (
              <span css={channelChipStyle}>{train.promotedChannel.toUpperCase()}</span>
            ) : (
              <Text>N/A</Text>
            )}
          </div>
          <Text size='small' css={mutedStyle}>
            {train.trainId}
          </Text>
        </div>
      </div>

      <div css={metaGridStyle}>
        <MetaItem label='Created by' value={train.createdBy} />
        <MetaItem
          label='Created at'
          value={
            train.createdAt?.trim() && dayjs(train.createdAt).isValid()
              ? dayjs(train.createdAt).format('DD MMM YYYY hh:mm A')
              : 'N/A'
          }
        />
        <MetaItem label='Promoted by' value={train.channelPromotedBy} />
        <MetaItem label='Artifacts' value={String(artifacts.length)} />
      </div>

      <div css={sectionStyle}>
        <div css={sectionHeaderStyle}>
          <Text weight='bold'>Artifacts</Text>
          <SearchField
            {...searchFieldProps}
            name='artifact-search'
            placeholder='Search by package name, artifact ID or version tag'
            css={searchFieldStyle}
          />
        </div>
        {visibleArtifacts.length > 0 ? (
          <>
            <div css={artifactGridStyle}>
              {visibleArtifacts.map((artifact, index) => (
                <TrainArtifactCard
                  key={
                    artifact.artifactId ||
                    [artifact.packageName, artifact.versionTag, artifact.targetOs, artifact.targetArch, index].join(':')
                  }
                  artifact={artifact}
                />
              ))}
            </div>
            {totalPages > 1 && (
              <div css={paginationStyle}>
                <Pagination pageByPageSelector page={page} total={totalPages} handleChange={setPage} />
              </div>
            )}
          </>
        ) : (
          <div css={emptyArtifactsStyle} role='status'>
            <Text color='subdued'>
              {artifacts.length > 0
                ? 'No artifacts match that package name, artifact ID, or version tag.'
                : 'No artifacts are associated with this train.'}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

const MetaItem = ({ label, value }: MetaItemProps) => {
  return (
    <div css={metaItemStyle}>
      <Text size='small' css={mutedStyle}>
        {label}
      </Text>
      <Text css={metaValueStyle}>{getDisplayValue(value)}</Text>
    </div>
  );
};

// Strips the OS_TYPE_ / ARCHITECTURE_ prefixes returned by the API.
const formatEnum = (value?: string) => value?.replace(/^(OS_TYPE_|ARCHITECTURE_)/, '') || 'N/A';

const TrainArtifactCard = ({ artifact }: { artifact: Artifact }) => (
  <div css={cardStyle}>
    <Text weight='bold' css={packageStyle}>
      {getDisplayValue(artifact.packageName)}
    </Text>
    <Text css={versionStyle}>{getDisplayValue(artifact.versionTag)}</Text>

    <div css={detailsGridStyle}>
      <ArtifactDetailItem label='Target PMS' value={formatEnum(artifact.targetPms).toUpperCase()} />
      <ArtifactDetailItem label='Artifact ID' value={getDisplayValue(artifact.artifactId)} />
      <ArtifactDetailItem label='OS' value={formatEnum(artifact.targetOs)} />
      <ArtifactDetailItem label='Architecture' value={formatEnum(artifact.targetArch)} />
      <ArtifactDetailItem label='Created At' value={formatDate(artifact.createdAt)} />
      <ArtifactDetailItem
        label='URL'
        value={
          artifact.gcsArtifactUrl?.trim() ? (
            <a href={artifact.gcsArtifactUrl} target='_blank' rel='noreferrer' css={linkStyle}>
              View
            </a>
          ) : (
            'N/A'
          )
        }
      />
      <ArtifactDetailItem
        label='Checksum'
        value={getDisplayValue(artifact.sha256Checksum)}
        cssOverride={fullWidthStyle}
      />
    </div>
  </div>
);

interface ArtifactDetailItemProps {
  label: string;
  value: ReactNode;
  cssOverride?: SerializedStyles;
}

const ArtifactDetailItem = ({ label, value, cssOverride }: ArtifactDetailItemProps) => (
  <div css={[detailItemStyle, cssOverride]}>
    <Text size='medium' css={mutedStyle}>
      {label}
    </Text>
    <Text size='medium' css={artifactValueStyle}>
      {value}
    </Text>
  </div>
);

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-lg);
  padding-top: var(--fabric-space-padding-lg);
`;

const headerRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--fabric-space-gap-sm);
`;

const titleBlockStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xxs);
`;

const titleRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-sm);
  flex-wrap: wrap;
`;

const channelChipStyle = css`
  display: inline-block;
  padding: var(--fabric-space-padding-xxs) var(--fabric-space-padding-sm);
  border-radius: var(--fabric-radius-full);
  background-color: var(--fabric-color-neutral-5);
  border: var(--fabric-border-width-thin) solid var(--fabric-color-neutral-30);
  color: var(--fabric-color-neutral-90);
  font-size: var(--fabric-font-size-xs);
  font-weight: 600;
  letter-spacing: var(--fabric-typography-body-xs-letter-spacing);
`;

const metaGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--fabric-space-gap-md);
  padding: var(--fabric-space-padding-md);
  background-color: var(--fabric-color-neutral-5);
  border: var(--fabric-border-width-thin) solid var(--fabric-color-neutral-20);
  border-radius: var(--fabric-radius-md);
`;

const metaItemStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xxs);
`;

const metaValueStyle = css`
  color: var(--fabric-color-neutral-90);
  word-break: break-word;
`;

const mutedStyle = css`
  color: var(--fabric-color-neutral-50);
`;

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-md);
`;

const sectionHeaderStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--fabric-space-gap-sm);
  flex-wrap: wrap;
`;

const searchFieldStyle = css`
  width: 440px;
`;

const artifactGridStyle = css`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--fabric-space-gap-md);
`;

const paginationStyle = css`
  display: flex;
  justify-content: center;
`;

const emptyArtifactsStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: var(--fabric-space-padding-lg);
  border: var(--fabric-border-width-thin) dashed var(--fabric-color-neutral-30);
  border-radius: var(--fabric-radius-md);
  background-color: var(--fabric-color-neutral-5);
`;

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xs);
  padding: var(--fabric-card-padding);
  background-color: var(--fabric-card-background);
  border: var(--fabric-border-width-thin) solid var(--fabric-color-neutral-30);
  border-radius: var(--fabric-card-radius);
  min-width: 0;
  overflow: hidden;
`;

const packageStyle = css`
  color: var(--fabric-color-neutral-90);
  word-break: break-word;
`;

const versionStyle = css`
  color: var(--fabric-color-neutral-70);
  word-break: break-all;
`;

const detailsGridStyle = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--fabric-space-gap-lg);
  margin-top: var(--fabric-space-gap-md);
`;

const detailItemStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xxs);
  min-width: 0;
`;

const fullWidthStyle = css`
  grid-column: 1 / -1;
`;

const artifactValueStyle = css`
  color: var(--fabric-color-neutral-80);
  word-break: break-all;
`;

const linkStyle = css`
  color: var(--fabric-color-blue-50);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
